import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { SESSION_ENDING_MESSAGE_CODES, SKIP_ERROR_NOTIFY_HEADER } from '@core/constants';
import { SessionEndReason } from '@core/enums';
import { SessionService } from '@core/services';
import { ToastrService } from 'ngx-toastr';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * The normalised error this interceptor rethrows to callers.
 *
 * Previously this shape was an untyped inline object literal, so no `catchError`
 * downstream could be typed against it and components fell back to `Error`
 * (losing `status`) — see web review [4]. Type `catchError((error: ApiError) => …)`
 * when a caller needs to branch on `status`; the interceptor has already shown the
 * user-facing toast, so callers should recover, not re-notify.
 */
export interface ApiError {
    /** User-facing message; already toasted by this interceptor (unless skipNotify). */
    message: string;
    /** HTTP status code (0 for network failures). */
    status: number;
    messageCode: number | null;
    /** The raw Angular error, for logging/debugging only. */
    originalError: HttpErrorResponse;
}

/** Turns a failed request into one toast (unless the caller asked to report it itself) and a typed {@link ApiError}. */
export const apiErrorInterceptor: HttpInterceptorFn = (request, next) => {
    const toastr = inject(ToastrService);
    const injector = inject(Injector);

    // Whether the caller reports a failure itself. The header is a note to this interceptor only, so it is taken off before the
    // request leaves the browser (review MR !1 W30): the server never sees it, and no CORS allow-list has to permit it.
    const skipNotify = request.headers.has(SKIP_ERROR_NOTIFY_HEADER);
    const outgoing = skipNotify ? request.clone({ headers: request.headers.delete(SKIP_ERROR_NOTIFY_HEADER) }) : request;

    return next(outgoing).pipe(
        catchError((error: HttpErrorResponse) => {
            let errorMessage = 'An unknown error occurred';
            // TODO error messages by using server-provided/Client Error messages change by ruchi mam
            if (error.error instanceof ErrorEvent) {
                // Client-side or network error
                errorMessage = `Network Error: ${error.error.message}`;
            } else {
                // Backend returned an unsuccessful response code
                switch (error.status) {
                    case 400:
                        errorMessage = error?.error?.message || 'Bad Request: Please check your input data.';
                        break;
                    case 401:
                        // A plain 401 does not end the session — only messageCode 637 or 638 does.
                        errorMessage = error?.error?.message || 'Unauthorized: Please login again.';
                        break;
                    case 403:
                        errorMessage = error?.error?.message || 'Forbidden: You do not have permission to perform this action.';
                        break;
                    case 404:
                        errorMessage = error?.error?.message || 'Not Found: The requested resource was not found.';
                        break;
                    case 409:
                        errorMessage = error?.error?.message || 'Conflict: The resource already exists or there is a data conflict.';
                        break;
                    case 422:
                        errorMessage = error?.error?.message || 'Validation Error: Please check your input data.';
                        break;
                    case 500:
                        errorMessage = error?.error?.message || 'Internal Server Error: Something went wrong on the server.';
                        break;
                    case 502:
                        errorMessage = error?.error?.message || 'Bad Gateway: Server is temporarily unavailable.';
                        break;
                    case 503:
                        errorMessage = error?.error?.message || 'Service Unavailable: Server is temporarily unavailable.';
                        break;
                    case 0:
                        errorMessage = error?.error?.message || 'Network Error: Please check your internet connection.';
                        break;
                    default:
                        errorMessage = error?.error?.message || `Server Error (${error.status}): ${error.message}`;
                }
            }

            // Resolved lazily, on a failure: SessionService needs HttpClient, so resolving it
            // while HttpClient's interceptor chain is being built would be a DI cycle.
            const sessionService = injector.get(SessionService);

            // Some 401 message codes mean the session is over. What that *means*
            // belongs to SessionService — this interceptor only recognises the
            // response and reports it.
            const sessionEndReason = readSessionEndReason(error);
            if (sessionEndReason) {
                sessionService.endSession(sessionEndReason);
            }

            // Once a teardown is under way every failure is a consequence of it:
            // requests still in flight from the screen the user just left cannot
            // succeed against cleared credentials, and the server answers each with
            // "Invalid token". One red toast per orphaned request would bury the
            // single notice that actually explains what happened.
            if (!skipNotify && !sessionEndReason && !sessionService.isEndingSession) {
                toastr.error(errorMessage);
            }

            // Log the error for debugging
            console.error('API Error Interceptor:', {
                url: error.url,
                status: error.status,
                message: errorMessage,
                originalError: error
            });

            // Return a user-friendly error. `messageCode` is carried through
            // deliberately: without it a downstream `catchError` can see the status
            // but not *why* it failed, which is how the old duplicate-logout bug
            // went unnoticed.
            return throwError((): ApiError => ({
                message: errorMessage,
                status: error.status,
                messageCode: readMessageCode(error),
                originalError: error
            }));
        })
    );
};

/**
 * Maps a session-ending 401 to its reason, or `null` if the response does not
 * end the session. Deliberately independent of the stored auth token so that
 * EVERY request in a failing burst suppresses the generic error toast, not
 * just the one that happened to arrive first.
 */
function readSessionEndReason(error: HttpErrorResponse): SessionEndReason | null {
    if (error.status !== 401) {
        return null;
    }
    const messageCode = readMessageCode(error);
    if (messageCode === null) {
        return null;
    }
    return SESSION_ENDING_MESSAGE_CODES.get(messageCode) ?? null;
}

/** Servers send `messageCode` as either a number or a numeric string. */
function readMessageCode(error: HttpErrorResponse): number | null {
    const rawCode: unknown = (error?.error as { messageCode?: unknown } | null)?.messageCode;
    if (rawCode === null || rawCode === undefined || rawCode === '') {
        return null;
    }
    const parsedCode = Number(rawCode);
    return Number.isNaN(parsedCode) ? null : parsedCode;
}
